import type { ReactNode } from 'react';
import type { StudioStatusKind } from './StatusBadge';
import type { StudioEntityMeta, StudioTag } from './entity-meta';

/**
 * Normalized shape this adapter's three real call sites each map their own
 * fields into: `Integration` and `CatalogEntry`
 * (components/ask-moo/types/integration.types.ts) and `ConnectableProvider`
 * (libs/moo/src/lib/types/connected-apps.ts). Unlike `toToolEntity` (which
 * adapts the one real `Tool` type directly), these three domain types share
 * no common field-naming scheme — `Integration.name` is the only display
 * name it has, while `CatalogEntry`/`ConnectableProvider` each split a
 * provider key (`provider`) from a separate display name (`display_name` /
 * `displayName`) — so there is no single real type this adapter could import
 * verbatim. Field names below are this adapter's own normalized contract,
 * not a guess at any one real type.
 *
 * The plan's initial guess (`{ name, displayName, description, iconKey,
 * toolCount?, status? }`) doesn't match any of the three either. Reconciled
 * per OMNI-828 Task 7 Step 2:
 * - `iconKey` implied a string lookup key (the skills-style `icon: "grid"`
 *   leak `toSkillEntity` documents). Real provider icons render through
 *   `ProviderIcon` (components/ask-moo/components/ProviderIcon.tsx), a
 *   component needing `provider`+`logoUrl`+`displayName` — building that
 *   element is call-site work, not this adapter's. `icon` here is an
 *   already-built `ReactNode`, passed through unchanged.
 * - `status` likewise: `Integration.status` is a plain 3-value string,
 *   `CatalogEntry` carries no status field at all (connectedness is derived
 *   from a separate integrations list), and `ConnectableProvider` needs
 *   `isConnectionBroken()` to derive a real tri-state (connected / broken /
 *   not connected). One shared string->kind lookup can't cover three
 *   incompatible vocabularies, so — matching `StatusBadge`'s own documented
 *   convention ("later tasks pass domain states through a per-surface
 *   Record<string, StudioStatusKind>") — each call site keeps its own small
 *   local mapping and hands this adapter an already-resolved
 *   `{ kind, label }`, which is passed through unchanged.
 * - `name`/`displayName` matches none of the three real types verbatim
 *   either. Uses `id` (the routing/uniqueness key every caller already has:
 *   `integration.id`, `entry.provider`, `provider.provider`) + `name` (the
 *   human title), matching `SkillLike`'s own `id`/`name` convention.
 */
export interface ProviderLike {
  readonly id: string;
  readonly name: string;
  /**
   * A short line shown tightly under the title (row/card variants only).
   * Only `ConnectableProvider` callers need this — a "what connecting this
   * lets Moo do" blurb (`describeProvider`) — alongside a second, distinct
   * `description` line (the connected account label). The other two call
   * sites have just one descriptive string and map it to `description`
   * instead, leaving this undefined.
   */
  readonly subtitle?: string;
  readonly description?: string;
  readonly icon?: ReactNode;
  /**
   * `undefined` — this call site has no tool-count concept at all (catalog,
   * connected apps): omit the tag entirely.
   * `null` — the backend didn't report a count (mirrors `Integration`'s
   * existing `tool_count == null` case): render the em-dash fallback that
   * view already had.
   * `number` — render the singular-aware "N tool(s)" label.
   */
  readonly toolCount?: number | null;
  /** Catalog-only per the brief: "NATIVE/MCP becomes an info tag on the
   *  catalog only". This adapter has no opinion on which callers pass it —
   *  it just renders the tag when present. */
  readonly type?: 'native' | 'mcp';
  readonly status?: { readonly kind: StudioStatusKind; readonly label: string };
}

/** "1 tool" vs "2 tools" — moved here from MooStudioIntegrationsView.tsx's
 *  own private helper (quick-wins Task 7) now that tool count renders as a
 *  tag through this shared adapter instead of view-local plain text. */
function formatToolCount(count: number): string {
  return `${count} ${count === 1 ? 'tool' : 'tools'}`;
}

const PROVIDER_TYPE_LABEL: Record<'native' | 'mcp', string> = {
  native: 'Native',
  mcp: 'MCP',
};

export function toProviderEntity(provider: ProviderLike): StudioEntityMeta {
  const tags: StudioTag[] = [];

  if (provider.type) {
    tags.push({
      label: PROVIDER_TYPE_LABEL[provider.type] ?? PROVIDER_TYPE_LABEL.native,
      tone: 'info',
    });
  }
  if (provider.toolCount !== undefined) {
    const label =
      provider.toolCount === null ? '—' : formatToolCount(provider.toolCount);
    tags.push({ label, tone: 'info' });
  }

  return {
    id: provider.id,
    title: provider.name,
    subtitle: provider.subtitle,
    description: provider.description,
    icon: provider.icon,
    tags: tags.length > 0 ? tags : undefined,
    status: provider.status,
  };
}
